import BlockContent from '@sanity/block-content-to-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { RawPortableText } from '../../types/types';
import serializers from '../serializers';
import { clsx } from '../utils/utils';
import Accordion from './Accordion';
import Button from './Button';

const SectionContainer = styled.section`
  width: 100%;
`;

const Section = styled.div`
  position: relative;
  width: 100%;
  margin: auto;

  &.withMinHeight {
    min-height: auto;

    @media (min-width: var(--layout-tablet-horizontal)) {
      min-height: 380px;
    }
  }
`;

const Title = styled.h2`
  margin-bottom: var(--large-size);
`;

const TabbedListContainerDesktop = styled.div`
  position: relative;
  width: 100%;
  display: none;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const TabsTitleContainer = styled.div`
  max-width: 40%;
  width: 100%;
  padding: var(--larger-size);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--smallest-size);
`;

const TabTitle = styled.nav`
  transition: all 0.3s ease;
  width: 100%;
  padding: var(--x-small-size);
  background-color: var(--tts-light-grey);
  border-radius: calc(var(--radius-sm) / 2);
  border: 1px solid var(--tts-light-grey);
  color: var(--tts-almost-black);
  cursor: pointer;

  &.active {
    background-color: var(--tts-white);
    color: var(--tts-black);
    border: 1px solid var(--tts-light-grey);
    pointer-events: none;
  }
`;

const TabsContainer = styled.div`
  max-width: 60%;
  width: 100%;
  min-height: 600px;
  height: 100%;
  padding: var(--larger-size);
  background-color: var(--tts-light-grey);
  display: flex;
  flex-direction: column;
`;

const TextContainerTitle = styled.h3`
  margin-bottom: var(--small-size);
`;

const TabsTextContainer = styled.div`
  display: none;

  &.active {
    display: block;
    width: 100%;
    margin-bottom: var(--large-size);
  }
`;

const Text = styled(BlockContent)``;

const TabbedListContainerMobile = styled.div`
  padding: var(--small-size);
  display: flex;
  flex-direction: column;
  background-color: var(--tts-light-grey);
  @media (min-width: 768px) {
    display: none;
  }
`;

const AccordionsContainer = styled.div`
  margin-bottom: var(--large-size);
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin-top: auto;
  margin-bottom: var(--large-size);
  background-color: var(--tts-black);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--x-small-size);

  @media (min-width: 520px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ToggleContainer = styled.div`
  margin-top: var(--small-size);
  display: flex;
  align-items: center;
  gap: var(--x-small-size);
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 30px;
  padding: 0px;

  .disabled & {
    pointer-events: none;
  }

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.4s ease 0s;
  border-radius: 34px;

  input:focus + & {
    box-shadow: 0 0 1px var(--tts-blue);
  }

  input:checked + & {
    background-color: var(--tts-blue);
  }
  input:not(:checked) + & {
    background-color: var(--tts-red);
  }
  .disabled input + &,
  .disabled input + & {
    background-color: var(--tts-dark-grey);
  }

  &::before {
    position: absolute;
    content: '';
    height: 26px;
    width: 26px;
    left: 1px;
    bottom: 1px;
    background-color: var(--tts-white);
    box-shadow: rgba(0, 0, 0, 0.25) 0px 5px 15px 0px;
    border-width: 1px;
    border-style: solid;
    border-color: rgb(242, 242, 242);
    transition: all 0.4s ease 0s;
    border-radius: 50%;

    input:checked + &,
    .toggleContainer.disabled & {
      transform: translateX(20px);
    }
  }
`;

const ToggleLabel = styled.span`
  color: var(--tts-red);

  .enabled & {
    color: var(--tts-blue);
  }

  .disabled & {
    color: var(--tts-dark-grey);
  }
`;

const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  word-wrap: normal;
`;

export interface Tab {
  title: string;
  text: RawPortableText;
  toggleButton?: {
    id: string;
    initialState?: boolean;
    disabled?: boolean;
  };
}

export type TabbedCardListProps = {
  title?: string;
  tabs: Array<Tab>;
  buttons?: Array<{
    title: string;
    onButtonClick: (togglesStates: Record<string, boolean>) => void;
    checkEnabled?: (togglesStates: Record<string, boolean>) => boolean;
  }>;
  className?: string;
};

const TabbedCardList = ({
  title,
  tabs,
  buttons,
  className,
}: TabbedCardListProps): React.ReactElement => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [togglesStates, setTogglesStates] = useState<Record<string, boolean>>(
    tabs.reduce(
      (states, { toggleButton }) =>
        toggleButton
          ? { ...states, [toggleButton.id]: toggleButton.initialState || false }
          : states,
      {},
    ),
  );

  const handleToggle = (toggleId: string) => {
    setTogglesStates(prevStates => {
      const newState = { ...prevStates };
      newState[toggleId] = !newState[toggleId];
      return newState;
    });
  };

  function renderToggleButton(toggleButton: NonNullable<Tab['toggleButton']>, label: string) {
    return (
      <ToggleContainer
        className={clsx(
          toggleButton.disabled && 'disabled',
          togglesStates[toggleButton.id] && 'enabled',
        )}
      >
        <Toggle>
          <VisuallyHidden>{label}</VisuallyHidden>
          <input
            disabled={toggleButton.disabled}
            type="checkbox"
            checked={togglesStates[toggleButton.id]}
            onChange={() => {
              handleToggle(toggleButton.id);
            }}
          />
          <Slider></Slider>
        </Toggle>
        <ToggleLabel>{togglesStates[toggleButton.id] ? 'Enabled' : 'Disabled'}</ToggleLabel>
      </ToggleContainer>
    );
  }

  return (
    <SectionContainer className={className}>
      <Section>
        {title && <Title>{title}</Title>}
        <TabbedListContainerDesktop>
          <TabsTitleContainer>
            {tabs.map((tab, i) => (
              <TabTitle
                className={clsx(activeTabIndex === i && 'active')}
                key={i}
                onClick={() => {
                  setActiveTabIndex(i);
                }}
              >
                {tab.title}
              </TabTitle>
            ))}
          </TabsTitleContainer>
          <TabsContainer>
            {tabs.map((tab, i) => (
              <TabsTextContainer className={clsx(activeTabIndex === i && 'active')} key={i}>
                <TextContainerTitle>{tab.title}</TextContainerTitle>
                <Text blocks={tab.text} renderContainerOnSingleChild serializers={serializers} />
                {tab.toggleButton &&
                  renderToggleButton(tab.toggleButton, `Enable or disable ${tab.title}`)}
              </TabsTextContainer>
            ))}
            {buttons && <Divider />}
            {buttons && (
              <ButtonsContainer>
                {buttons.map((button, i) => (
                  <Button
                    backgroundColour="blue"
                    key={i}
                    disabled={button.checkEnabled ? !button.checkEnabled(togglesStates) : false}
                    onClick={() => {
                      button.onButtonClick(togglesStates);
                    }}
                  >
                    {button.title}
                  </Button>
                ))}
              </ButtonsContainer>
            )}
          </TabsContainer>
        </TabbedListContainerDesktop>
        <TabbedListContainerMobile>
          <AccordionsContainer>
            {tabs.map((tab, i) => (
              <Accordion key={i} title={tab.title} startsOpen>
                <Text blocks={tab.text} renderContainerOnSingleChild serializers={serializers} />
                {tab.toggleButton &&
                  renderToggleButton(tab.toggleButton, `Enable or disable ${tab.title}`)}
              </Accordion>
            ))}
          </AccordionsContainer>
          {buttons && <Divider />}
          {buttons && (
            <ButtonsContainer>
              {buttons.map((button, i) => (
                <Button
                  key={i}
                  backgroundColour="blue"
                  disabled={button.checkEnabled ? !button.checkEnabled(togglesStates) : false}
                  onClick={() => {
                    button.onButtonClick(togglesStates);
                  }}
                >
                  {button.title}
                </Button>
              ))}
            </ButtonsContainer>
          )}
        </TabbedListContainerMobile>
      </Section>
    </SectionContainer>
  );
};

export default TabbedCardList;
